import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, User, AlertCircle, Copy, Check } from 'lucide-react';
import MarkdownRenderer from '../shared/MarkdownRenderer';

const MessageList = ({ messages, onRetry, showScrollFab, onScrollChange }) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    if (!showScrollDown) scrollToBottom();
  }, [messages, showScrollDown, scrollToBottom]);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setShowScrollDown(!nearBottom);
    onScrollChange?.(!nearBottom);
  };

  const copyText = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="ai-tutor-messages-wrap">
      <div
        className="ai-tutor-messages"
        ref={containerRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {messages.map((message) => {
          const { role, content, timestamp, isError, id } = message;
          const isUser = role === 'user';
          const isAssistant = role === 'assistant';

          return (
            <div key={id} className={`ai-tutor-message ${role} ${isError ? 'error' : ''}`}>
              <div className="ai-tutor-message-avatar" aria-hidden>
                {isUser ? <User size={16} /> : isError ? <AlertCircle size={16} /> : <Bot size={16} />}
              </div>
              <div className="ai-tutor-message-content">
                <div className={`ai-tutor-message-bubble ${isError ? 'error' : ''}`}>
                  {isAssistant ? (
                    <MarkdownRenderer content={content} />
                  ) : (
                    <p className="ai-tutor-message-text">{content}</p>
                  )}
                </div>
                <div className="ai-tutor-message-footer">
                  <span className="ai-tutor-message-time">{formatTime(timestamp)}</span>
                  <div className="qai-msg-actions">
                    {(isAssistant || isUser) && (
                      <button
                        type="button"
                        className="qai-msg-action-btn"
                        onClick={() => copyText(id, content)}
                      >
                        {copiedId === id ? <Check size={12} /> : <Copy size={12} />}
                        {copiedId === id ? t('aiTutoring.copied') : t('aiTutoring.copy')}
                      </button>
                    )}
                    {isError && onRetry && (
                      <button type="button" className="qai-msg-action-btn" onClick={onRetry}>
                        {t('aiTutoring.retry')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {showScrollFab && showScrollDown && (
        <button
          type="button"
          className="qai-scroll-fab"
          onClick={() => scrollToBottom()}
          aria-label={t('aiTutoring.scrollToBottom')}
        >
          ↓
        </button>
      )}
    </div>
  );
};

export default MessageList;